/**
 * SubscriptionUpsell — конверсионные баннеры для FREE юзеров.
 *
 * 2 варианта:
 * 1. <FeedUpsellBanner /> — sticky-style баннер для /feed страницы.
 *    Показывается только если у юзера нет активной подписки.
 *
 * 2. <ContactLimitModal /> — full-screen модал когда юзер исчерпал 5/5 контакта.
 *    Показывается вместо сухого alert «Ліміт вичерпано».
 *
 * Conversion principles:
 * - Loss aversion (что юзер ТЕРЯЕТ)
 * - Specific benefits (миттєві вакансії, контакти без лімітів, AI)
 * - Single CTA → /profile#subscription
 * - Soft dismiss (юзер не чувствует pressure)
 */

import { createPortal } from "react-dom";
import { PopupCloseButton } from "@/components/PopupClose";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMySubscription } from "@/api/hooks";
import { isActiveSubscription } from "@/api/types";
import { SUBSCRIPTION_BENEFITS } from "@/lib/benefits";
import { SubscriptionCard } from "@/components/SubscriptionCard";
import { TgEmoji } from "@/components/TgEmoji";
import { popupActionsCol, popupBtnPrimary } from "@/lib/popupButtons";

/**
 * Sticky banner для feed-стрічки. Показывается только если юзер FREE.
 * Использовать в начале списка вакансий (после Hero).
 */
export function FeedUpsellBanner() {
  const subQuery = useMySubscription();
  if (subQuery.isLoading || subQuery.isError) return null;
  const sub = subQuery.data!;
  if (isActiveSubscription(sub)) return null; // активной подписке банер не нужен

  return (
    <div className="mx-5 mt-3">
      {/* Оформлення — спільний `SubscriptionCard` (owner 2026-09-10). Раніше
          тут була своя копія темної картки: інший радіус, інший кегль, стрілка
          32 замість 36 і `items-center`, через який плитка ставала навпроти
          середини абзацу. */}
      <SubscriptionCard
        icon="bolt"
        title={
          <>
            Оформи підписку, щоб{" "}
            <span className="font-serif italic text-primary">
              зняти обмеження
            </span>
          </>
        }
        text={
          <>
            Без підписки вакансії приходять із{" "}
            <b className="font-semibold text-white/90">затримкою 4 години</b>, а
            контактів можна відкрити{" "}
            <b className="font-semibold text-white/90">
              пʼять на тиждень
            </b>
            .
          </>
        }
      />
    </div>
  );
}

/**
 * Full-screen модал когда юзер исчерпал 5/5 контактов.
 * Используется в FeedVacancy / Feed inline-expand вместо alert.
 */
export function ContactLimitModal({
  open,
  onClose,
  remaining = 0,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Скільки відкриттів лишилось ПІСЛЯ цього: 0 — ліміт вичерпано,
   * 1 — останнє (owner 2026-09-08: говорити і на четвертому контакті).
   */
  remaining?: 0 | 1;
}) {
  if (!open) return null;

  // createPortal у document.body (owner 2026-07-10): без цього попап рендериться
  // всередині Layout-контейнера (max-w-md + overflow) → «рамки по боках» і
  // криве позиціювання. Портал прибиває його до body → fixed = viewport.
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm bg-surface rounded-3xl p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Хрестик замість кнопки «Почекаю понеділка» / «Зрозуміло» —
            економимо місце (owner 2026-09-27). */}
        <PopupCloseButton onClose={onClose} />
        <div className="text-center mb-5">
          <div className="mb-3 flex justify-center">
            <TgEmoji char={remaining === 1 ? "🔑" : "🔒"} size={52} />
          </div>
          <div className="font-display text-[1.25rem] font-bold text-ink leading-tight [text-wrap:balance]">
            {remaining === 1
              ? "Лишилось одне відкриття"
              : "Цього тижня ти вже відкрив 5 контактів"}
          </div>
          <div className="text-[0.8125rem] text-ink-dim mt-2.5 leading-relaxed [text-wrap:balance]">
            {remaining === 1
              ? // Owner 2026-09-08: «останній контакт» читалось так, ніби
                // відкриттів більше немає. Кажемо прямо, скільки лишилось.
                "У тебе лишилось ще одне відкриття цього тижня. Наступні зʼявляться в понеділок."
              : "Це твій безкоштовний ліміт — він оновиться в понеділок. З підпискою відкриттів без обмежень."}
          </div>
        </div>

        {/* Перелік переваг — із ЄДИНОГО джерела (`lib/benefits.ts`), а не
            своїм списком: owner 2026-09-08 побачив, що тут лише чотири
            пункти, хоча фактично їх більше. */}
        <div className="rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 p-4 mb-5">
          <div className="font-display text-[0.8125rem] font-bold text-ink mb-2.5">
            З підпискою ти отримуєш:
          </div>
          <ul className="space-y-2 text-[0.78125rem] text-ink/85">
            {SUBSCRIPTION_BENEFITS.map((b) => (
              <li key={b.title} className="flex items-start gap-2">
                <span className="shrink-0">{b.icon}</span>
                <span>
                  <b>{b.title}</b>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className={popupActionsCol}>
          <Link to="/subscription" onClick={onClose} className={popupBtnPrimary}>
            Обрати тариф
          </Link>
        </div>
      </div>
    </div>,
    document.body,
  );
}


/**
 * Спільна логіка попапа для обох місць, звідки відкривають контакти
 * (стрічка і сторінка вакансії), щоб та сама умова не жила у двох копіях.
 *
 * Викликати `check(res)` в `onSuccess` мутації і рендерити `node`.
 * Попап спливає ТІЛЬКИ коли відкриття справді списалось (`spent`): перегляд
 * уже відкритої вакансії його не піднімає.
 */
export function useContactLimitPopup() {
  const [remaining, setRemaining] = useState<0 | 1 | null>(null);

  const check = (res: { attempts_left?: number; spent?: boolean } | undefined) => {
    if (!res?.spent) return;
    if (res.attempts_left === 1) setRemaining(1);
    else if (res.attempts_left === 0) setRemaining(0);
  };

  const node = (
    <ContactLimitModal
      open={remaining !== null}
      remaining={remaining ?? 0}
      onClose={() => setRemaining(null)}
    />
  );

  return { node, check };
}
